import React, { useRef, useState } from "react";
import { Table, Input, InputNumber, Empty, Space, Button, Switch, Tag } from "antd";
import styles from "./ProductBaseSupplierTable.module.scss";
import Highlighter from "react-highlight-words";

const ProductBaseSupplierTable = ({
  products,
  onUpdateProduct,
  supplierType,
  onSaveProduct,
  savingProductIds,
  modifiedProductIds,
}) => {
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0] || "");
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters?.();
    setSearchText("");
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div
        style={{ padding: 8 }}
        dir="rtl"
        onKeyDown={(event) => event.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder="نام محصول را وارد کنید"
          value={selectedKeys[0] || ""}
          onChange={(event) =>
            setSelectedKeys(event.target.value ? [event.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />

        <Space wrap>
          <Button
            type="primary"
            size="small"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          >
            فیلتر
          </Button>

          <Button
            size="small"
            onClick={() => {
              if (clearFilters) {
                handleReset(clearFilters);
                confirm();
              }
            }}
          >
            پاک کردن
          </Button>

          <Button type="link" size="small" onClick={() => close()}>
            بستن
          </Button>
        </Space>
      </div>
    ),

    onFilter: (value, record) =>
      String(record[dataIndex] ?? "")
        .toLowerCase()
        .includes(String(value ?? "").toLowerCase()),

    filterDropdownProps: {
      onOpenChange(open) {
        if (open) {
          searchInput.current?.select();
        }
      },
    },

    render: (value) => {
      const text = String(value ?? "");

      return searchedColumn === dataIndex && searchText ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text}
        />
      ) : (
        <span className={styles.productName}>{text}</span>
      );
    },
  });
  const isFood = supplierType === "food";

  const columns = [
    {
      title: "شناسه",
      dataIndex: "id",
      key: "id",
      width: 100,
    },
    {
      title: "نام محصول",
      dataIndex: "name",
      key: "name",
      width: 250,
      ...getColumnSearchProps("name"),
    },
    {
      title: isFood ? "موجود" : "تعداد",
      dataIndex: "count",
      key: "count",
      width: isFood ? 130 : 140,
      align: isFood ? "center" : undefined,
      render: (value, record) => {
        if (isFood) {
          const isChecked = Number(value) > 0;
          return (
            <Switch
              checked={isChecked}
              checkedChildren="موجود"
              unCheckedChildren="ناموجود"
              onChange={(checked) =>
                onUpdateProduct(record.id, "count", checked ? 500 : 0)
              }
            />
          );
        }

        return (
          <InputNumber
            value={value === "" ? null : Number(value)}
            min={0}
            precision={0}
            style={{ width: "100%" }}
            onChange={(newValue) =>
              onUpdateProduct(
                record.id,
                "count",
                newValue == null ? "" : String(newValue),
              )
            }
          />
        );
      },
    },
    {
      title: "قیمت",
      dataIndex: "price",
      key: "price",
      width: 180,
      render: (value, record) => (
        <InputNumber
          controls={false}
          value={value === "" ? null : Number(value)}
          min={0}
          precision={0}
          style={{ width: "100%" }}
          formatter={(value) =>
            value === undefined || value === null
              ? ""
              : `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
          }
          parser={(value) => value.replace(/,/g, "")}
          onChange={(newValue) =>
            onUpdateProduct(
              record.id,
              "price",
              newValue == null ? "" : String(newValue),
            )
          }
        />
      ),
    },
    {
      title: "قیمت تخفیف",
      dataIndex: "off_price",
      key: "off_price",
      width: 180,
      render: (value, record) => (
        <InputNumber
          controls={false}
          value={value === "" ? null : Number(value)}
          min={0}
          precision={0}
          style={{ width: "100%" }}
          formatter={(value) =>
            value === undefined || value === null
              ? ""
              : `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
          }
          parser={(value) => value.replace(/,/g, "")}
          onChange={(newValue) =>
            onUpdateProduct(
              record.id,
              "off_price",
              newValue == null ? "" : String(newValue),
            )
          }
        />
      ),
    },
    {
      title: "آخرین بروزرسانی",
      dataIndex: "update_date",
      key: "update_date",
      width: 180,
      align: "center",
      sorter: (a, b) => Number(a.update_date || 0) - Number(b.update_date || 0),
      defaultSortOrder: "descend",
      filters: [
        { text: "۲۴ ساعت گذشته", value: "24h" },
        { text: "هفته اخیر (تا ۷ روز)", value: "week" },
        { text: "ماه اخیر (تا ۳۰ روز)", value: "month" },
      ],
      filterMultiple: false,
      onFilter: (value, record) => {
        const days = Number(record.update_date || 0);
        if (value === "24h") return days === 0;
        if (value === "week") return days <= 7;
        if (value === "month") return days <= 30;
        return true;
      },
      render: (value) => {
        const days = Number(value || 0);
        if (days === 0) {
          return <Tag color="green">کمتر از ۲۴ ساعت گذشته</Tag>;
        }
        if (days <= 7) {
          return <Tag color="blue">{days} روز گذشته</Tag>;
        }
        if (days <= 30) {
          return <Tag color="orange">{days} روز گذشته</Tag>;
        }
        return <Tag color="default">{days} روز گذشته</Tag>;
      },
    },
    {
      title: "عملیات",
      key: "action",
      width: 110,
      align: "center",
      render: (_, record) => {
        const isModified = modifiedProductIds?.[record.id];
        const isSaving = savingProductIds?.[record.id];

        if (!isModified && !isSaving) {
          return <span style={{ color: "#bfbfbf" }}>—</span>;
        }

        return (
          <Button
            type="primary"
            size="small"
            loading={isSaving}
            onClick={() => onSaveProduct && onSaveProduct(record)}
            style={{
              backgroundColor: isSaving ? undefined : "#c20c64",
              borderColor: "#c20c64",
              borderRadius: "4px",
              fontSize: "12px",
            }}
          >
            ذخیره
          </Button>
        );
      },
    },
  ];

  return (
    <div className={styles.tableContainer} dir="rtl">
      <Table
        rowKey="id"
        columns={columns}
        dataSource={products}
        locale={{
          emptyText: <Empty description="محصولی یافت نشد." />,
          filterConfirm: "فیلتر",
          filterReset: "پاک کردن",
          filterEmptyText: "موردی یافت نشد",
        }}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50"],
          showTotal: (total) => `تعداد کل: ${total} محصول`,
        }}
        scroll={{ x: 1150 }}
        size="middle"
      />
    </div>
  );
};

export default ProductBaseSupplierTable;
